import React, { useState, useEffect } from "react";
import DefaultButton from "../../styles/materialButtonStyle";
import MaterialDialog from "../../styles/materialDialog";
import { TextField } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import axios from "axios";
import userAuthenticationConfig from "../../../utils/userAuthenticationConfig";
import { useTranslation } from "react-i18next";

import {
  StyledDialogAction,
  StyledDialogContent,
  StyledDialogHead,
  StyledDialogWrapper
} from "../../styles/styledDialog";
import { parseAxiosError } from "../../../utils/response";
import { HTTP_STATUSES, SERVICES, STANDARD_ERRORS_WAY } from "../../../utils/consts";
import Notification from "../../elements/notification/Notification";
import { StyledInvoiceWrapper } from "../../elements/invoiceDialog/styledInvoice";
import CurrenciesAndPaymentSystemByServiceRelationshipsSelect
  from "../../elements/serviceReletionships/CurrenciesAndPaymentSystemByServiceRelationshipsSelect";
import { validateCryptoWallet } from "../../../utils/attributesValidator";

const CreateWalletWhitelistButton = ({ notification, setNotification }) => {
  const { t } = useTranslation("walletWhitelistPage");

  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const defaultData = {
    "currency": null,
    "paymentSystem": null,
    "wallet": null,
  };

  const [submitData, setSubmitData] = useState(defaultData);
  const [attributes, setAttributes] = useState(null);

  useEffect(() => {
    if (submitData.currency && submitData.paymentSystem) {
      setAttributes(null);
      setSubmitData(prev => ({ ...prev, wallet: null }));
      axios.get(
        "/api/transaction-attributes?currency.asset=" + submitData.currency + "&paymentSystem.subName=" + submitData.paymentSystem + "&service.name=" + SERVICES.ALP_PAY,
        userAuthenticationConfig()
      ).then(response => {
        if (response.status === 200) {
          setAttributes(response.data["hydra:member"]);
        }
      }).catch(error => {
        parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
      });
    } else {
      setAttributes(null);
    }
  }, [submitData.currency, submitData.paymentSystem]);

  const handleSubmit = (event) => {
    event.preventDefault();

    setDisabled(true);

    if (!submitData.currency || !submitData.paymentSystem) {
      setNotification({
        ...notification,
        visible: true,
        type: "error",
        message: t("createForm.errors.checkFieldsMessage")
      });
      setDisabled(false);

      return;
    }

    const attributeValues = {};
    let hasEmptyAttribute = false;

    if (attributes) {
      for (const attr of attributes) {
        if (attr.type === "hidden") continue;
        if (!attr.value) {
          hasEmptyAttribute = true;
          break;
        }
        attributeValues[attr.name] = attr.value;
      }
    }

    if (hasEmptyAttribute) {
      setNotification({
        ...notification,
        visible: true,
        type: "error",
        message: t("createForm.errors.checkFieldsMessage")
      });
      setDisabled(false);

      return;
    }

    if (!validateCryptoWallet(submitData.currency, submitData.wallet, submitData.paymentSystem)) {
      setNotification({ ...notification, visible: true, type: "error", message: t("createForm.errors.invalidWallet") });
      setDisabled(false);

      return;
    }

    const payload = {
      currency: submitData.currency,
      paymentSystem: submitData.paymentSystem,
      ...attributeValues,
    };

    axios.post("/api/wallet/user-whitelist/create", payload, userAuthenticationConfig(false)).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_CREATED) {
        handleClose();
        setNotification({ ...notification, visible: true, type: "success", message: t("createForm.successMessage") });
      }
    }).catch(error => {
      handleClose();
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    }).finally(() => setDisabled(false));
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setSubmitData(defaultData);
    setAttributes(null);
    setOpen(false);
  };

  const renderAttributes = () => {
    if (!attributes) return null;

    if (attributes.length === 0) {
      return <Alert severity="info">{t("createForm.noAttributes")}</Alert>;
    }

    return attributes.map((attribute, key) => {
      if (attribute.type === "hidden") return null;

      return <TextField
        key={key}
        id={attribute.name}
        label={attribute.title}
        placeholder={attribute.placeholder}
        value={attribute.value || ""}
        onChange={(data) => {
          const trimmedValue = data.target.value.trim();
          let values = [...attributes];
          values[key]["value"] = trimmedValue;
          setAttributes(values);

          if (attribute.name === "wallet") {
            setSubmitData(prev => ({ ...prev, wallet: trimmedValue }));
          }
        }}
      />;
    });
  };

  return (
    <>
      <Notification
        notification={notification}
        setNotification={setNotification}
      />
      <StyledInvoiceWrapper className="wallet-main-whitelist-head__create">
        <DefaultButton
          color="success"
          onClick={handleClickOpen}
        >
          {t("createForm.create")}
        </DefaultButton>
        <MaterialDialog
          open={open}
        >
          <StyledDialogWrapper>
            <StyledDialogHead>
              {t("createForm.title")}
            </StyledDialogHead>
            <StyledDialogContent>

              <CurrenciesAndPaymentSystemByServiceRelationshipsSelect
                submitData={submitData}
                setSubmitData={setSubmitData}
              />
              {renderAttributes()}
            </StyledDialogContent>
            <StyledDialogAction col="2">
              <DefaultButton
                onClick={handleClose}
                color="danger"
                disabled={disabled}
              >
                {t("createForm.cancel")}
              </DefaultButton>
              <DefaultButton
                color="success"
                onClick={handleSubmit}
                disabled={disabled}
              >
                {t("createForm.create")}
              </DefaultButton>
            </StyledDialogAction>
          </StyledDialogWrapper>
        </MaterialDialog>
      </StyledInvoiceWrapper>
    </>
  );
};

export default CreateWalletWhitelistButton;